import { AnyAction } from 'redux';
import { AxiosPromise, AxiosResponse } from 'axios';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'data/root.reducers';
import { ServerResponse } from 'data/types';

export const API_PATH_CSP_CONFIG = '/csp';

export const GO_TO_CSP_CONFIG_LIST = 'GO_TO_CSP_CONFIG_LIST';
export const GET_CSP_CONFIG_LIST = 'GET_CSP_CONFIG_LIST';
export const GET_CSP_CONFIG_LIST_PENDING = 'GET_CSP_CONFIG_LIST_PENDING';
export const GET_CSP_CONFIG_LIST_FULFILLED = 'GET_CSP_CONFIG_LIST_FULFILLED';
export const GET_CSP_CONFIG = 'GET_CSP_CONFIG';
export const GET_CSP_CONFIG_PENDING = 'GET_CSP_CONFIG_PENDING';
export const GET_CSP_CONFIG_FULFILLED = 'GET_CSP_CONFIG_FULFILLED';
export const CREATE_CSP_CONFIG = 'CREATE_CSP_CONFIG';
export const CREATE_CSP_CONFIG_PENDING = 'CREATE_CSP_CONFIG_PENDING';
export const CREATE_CSP_CONFIG_FULFILLED = 'CREATE_CSP_CONFIG_FULFILLED';
export const CREATE_CSP_CONFIG_REJECTED = 'CREATE_CSP_CONFIG_REJECTED';
export const UPDATE_CSP_CONFIG = 'UPDATE_CSP_CONFIG';
export const UPDATE_CSP_CONFIG_FULFILLED = 'UPDATE_CSP_CONFIG_FULFILLED';
export const UPDATE_CSP_CONFIG_REJECTED = 'UPDATE_CSP_CONFIG_REJECTED';
export const DELETE_CSP_CONFIG = 'DELETE_CSP_CONFIG';
export const DELETE_CSP_CONFIG_FULFILLED = 'DELETE_CSP_CONFIG_FULFILLED';
export const DELETE_CSP_CONFIG_REJECTED = 'DELETE_CSP_CONFIG_REJECTED';
export const SELECT_CSP_CONFIG = 'SELECT_CSP_CONFIG';

export const OPEN_CSP_CONFIG_CREATE_PAGE = 'OPEN_CSP_CONFIG_CREATE_PAGE';
export const CLOSE_CSP_CONFIG_CREATE_PAGE = 'CLOSE_CSP_CONFIG_CREATE_PAGE';
export const OPEN_CSP_CONFIG_EDIT_PAGE = 'OPEN_CSP_CONFIG_EDIT_PAGE';
export const CLOSE_CSP_CONFIG_EDIT_PAGE = 'CLOSE_CSP_CONFIG_EDIT_PAGE';
export const SET_CSP_CONFIG_LOADING = 'SET_CSP_CONFIG_LOADING';
export const GO_TO_CSP_CONFIG_VIEW = 'GO_TO_CSP_CONFIG_VIEW';

export enum CspConfigPrivileges {
  View = 'cf_view',
  Edit = 'cf_write',
  Delete = 'cf_delete',
}

/**
 * Mirrors the Xcipio /api/v1/csp payload verbatim, lower case field names included.
 * A CSP is keyed by cspid; the single-object endpoints take that cspid base64 encoded,
 * which is what getCspConfigId returns.
 */
export type CspConfig = {
  cspid: string
  name: string
  legalcontact?: string
  nwcontact?: string
}

export function getCspConfigId(cspConfig?: CspConfig | null): string {
  return cspConfig?.cspid ? window.btoa(cspConfig.cspid) : '';
}

export interface CspConfigState {
  entities: CspConfig[]
  single: CspConfig | null
  selected: CspConfig[]
  createdId: string | null
  ui: CspConfigUIState
}

export type CspConfigUIState = {
  isCreatePopupOpen: boolean
  isEditPopupOpen: boolean
  isLoading: boolean
}

export interface GoToCspConfigListAction extends AnyAction {
  type: typeof GO_TO_CSP_CONFIG_LIST
}

export interface OpenCspConfigCreatePageAction extends AnyAction {
  type: typeof OPEN_CSP_CONFIG_CREATE_PAGE
}

export interface CloseCspConfigCreatePageAction extends AnyAction {
  type: typeof CLOSE_CSP_CONFIG_CREATE_PAGE
}

export interface OpenCspConfigEditPageAction extends AnyAction {
  type: typeof OPEN_CSP_CONFIG_EDIT_PAGE
}

export interface CloseCspConfigEditPageAction extends AnyAction {
  type: typeof CLOSE_CSP_CONFIG_EDIT_PAGE
}

export interface SetCspConfigLoadingAction extends AnyAction {
  type: typeof SET_CSP_CONFIG_LOADING
  payload: { isLoading: boolean }
}

export interface GetCspConfigListAction extends AnyAction {
  type: typeof GET_CSP_CONFIG_LIST
  payload: AxiosPromise<ServerResponse<CspConfig>>
}

export interface GetCspConfigListPendingAction extends AnyAction {
  type: typeof GET_CSP_CONFIG_LIST_PENDING
  payload: null
}

export interface GetCspConfigListFulfilledAction extends AnyAction {
  type: typeof GET_CSP_CONFIG_LIST_FULFILLED
  payload: AxiosResponse<ServerResponse<CspConfig>>
}

export interface GetCspConfigAction extends AnyAction {
  type: typeof GET_CSP_CONFIG
  payload: AxiosPromise<ServerResponse<CspConfig>>
}

export interface GetCspConfigPendingAction extends AnyAction {
  type: typeof GET_CSP_CONFIG_PENDING
  payload: null
}

export interface GetCspConfigFulfilledAction extends AnyAction {
  type: typeof GET_CSP_CONFIG_FULFILLED
  payload: AxiosResponse<ServerResponse<CspConfig>>
}

export interface CreateCspConfigThunkAction extends ThunkAction<void, RootState, void, AnyAction> {}
export interface CreateCspConfigAction extends AnyAction {
  type: typeof CREATE_CSP_CONFIG
  payload: AxiosPromise<ServerResponse<CspConfig>>
}

export interface CreateCspConfigPendingAction extends AnyAction {
  type: typeof CREATE_CSP_CONFIG_PENDING
  payload: null
}

export interface CreateCspConfigFulfilledAction extends AnyAction {
  type: typeof CREATE_CSP_CONFIG_FULFILLED
  payload: AxiosResponse<ServerResponse<CspConfig>>
}

export interface CreateCspConfigRejectedAction extends AnyAction {
  type: typeof CREATE_CSP_CONFIG_REJECTED
  payload: AxiosResponse<ServerResponse<CspConfig>>
}

export interface UpdateCspConfigThunkAction extends ThunkAction<void, RootState, void, AnyAction> {}
export interface UpdateCspConfigAction extends AnyAction {
  type: typeof UPDATE_CSP_CONFIG
  payload: AxiosPromise<ServerResponse<CspConfig>>
}

export interface UpdateCspConfigFulfilledAction extends AnyAction {
  type: typeof UPDATE_CSP_CONFIG_FULFILLED
  payload: AxiosResponse<ServerResponse<CspConfig>>
}

export interface UpdateCspConfigRejectedAction extends AnyAction {
  type: typeof UPDATE_CSP_CONFIG_REJECTED
  payload: AxiosResponse<ServerResponse<CspConfig>>
}

export interface DeleteCspConfigThunkAction extends ThunkAction<void, RootState, void, AnyAction> {}
export interface DeleteCspConfigAction extends AnyAction {
  type: typeof DELETE_CSP_CONFIG
  payload: AxiosPromise<ServerResponse<CspConfig>>
}

export interface DeleteCspConfigFulfilledAction extends AnyAction {
  type: typeof DELETE_CSP_CONFIG_FULFILLED
  payload: AxiosResponse<ServerResponse<CspConfig>>
}

export interface DeleteCspConfigRejectedAction extends AnyAction {
  type: typeof DELETE_CSP_CONFIG_REJECTED
  payload: AxiosResponse<ServerResponse<CspConfig>>
}

export interface SelectCspConfigAction extends AnyAction {
  type: typeof SELECT_CSP_CONFIG
  payload: CspConfig[]
}

export interface GoToCspConfigViewAction extends AnyAction {
  type: typeof GO_TO_CSP_CONFIG_VIEW
}

export type CspConfigActionTypes =
  GoToCspConfigListAction |
  OpenCspConfigCreatePageAction |
  CloseCspConfigCreatePageAction |
  OpenCspConfigEditPageAction |
  CloseCspConfigEditPageAction |
  SetCspConfigLoadingAction |
  GetCspConfigListAction |
  GetCspConfigListPendingAction |
  GetCspConfigListFulfilledAction |
  GetCspConfigAction |
  GetCspConfigPendingAction |
  GetCspConfigFulfilledAction |
  CreateCspConfigAction |
  CreateCspConfigPendingAction |
  CreateCspConfigFulfilledAction |
  CreateCspConfigRejectedAction |
  UpdateCspConfigAction |
  UpdateCspConfigFulfilledAction |
  UpdateCspConfigRejectedAction |
  DeleteCspConfigAction |
  DeleteCspConfigFulfilledAction |
  DeleteCspConfigRejectedAction |
  SelectCspConfigAction |
  GoToCspConfigViewAction;
